import { z } from 'zod';

/**
 * `{{variable}}` interpolation of an MQTT payload into a config-supplied string.
 *
 * Ported from legacy `utils/functions.ts` (`findVariableInText`) plus the substitution loop
 * that was copy-pasted into five widgets with two subtly different behaviours. Both
 * behaviours are preserved and selected explicitly — see `missing` below.
 * docs/MIGRATION-WIDGETS.md §3.
 */

/**
 * The legacy regex, verbatim: **letters only**, so `{{peopleCount2}}` and `{{people_count}}`
 * never match and render as literal braces.
 *
 * Preserved rather than widened. A config that currently relies on a variable *not* being
 * substituted — a literal `{{...}}` in body text — would start rendering differently, and
 * these files are deployed on live displays (CLAUDE.md constraint 1). Widening it is a
 * config-format change, not a bug fix.
 */
const VARIABLE_PATTERN = /{{[a-zA-Z]*(}})/g;

/** One entry of `options.textBloc.replace` / `.replaced`. */
export const textReplacementSchema = z.object({
  find: z.string(),
  replace: z.string(),
});

export type TextReplacement = z.infer<typeof textReplacementSchema>;

/**
 * What happens to a `{{variable}}` with no matching key in the payload. The legacy app has
 * both, one per group of widgets, and they are visibly different on a wall.
 *
 * - `'na'` — the variable is replaced with the literal `N/A`.
 *   Used by `TextWidget` and `DoughnutWidget`.
 * - `'strip'` — the variable is left alone, then a blanket brace strip runs over the whole
 *   string, so the bare variable *name* stays visible.
 *   Used by `HourlyChartWidget`, `FloorOccupancyWidget`, `WeatherWidget`, `DepartureWidget`
 *   and `BookingsWidget`.
 *
 * Note that `'strip'` removes every brace in the string, not only the ones around unresolved
 * variables — that is what the legacy `.replaceAll(/{|}/g, '')` does and a config with
 * braces in its body text depends on it.
 */
export type MissingVariableMode = 'na' | 'strip';

export type SubstituteOptions = {
  missing: MissingVariableMode;
  /**
   * Post-processing, applied after substitution. Spelled `options.textBloc.replace` in
   * `TextWidget`/`DoughnutWidget` and `options.textBloc.replaced` in `HourlyChartWidget`;
   * the caller passes whichever the config carries (both keys are read, neither config is
   * normalised).
   *
   * Each entry replaces the **first** occurrence of `find`, exactly as legacy `String.replace`
   * with a string pattern did.
   */
  replace?: readonly TextReplacement[];
};

/**
 * Every `{{variable}}` in `text`, brace-stripped, in order and including duplicates.
 *
 * Duplicates are kept on purpose: the substitution below replaces one occurrence per entry,
 * so a text mentioning `{{count}}` twice needs two entries to resolve both — which is the
 * legacy behaviour.
 */
export function findVariableInText(text: string): string[] {
  const matches = text.match(VARIABLE_PATTERN);
  if (matches === null) return [];
  return matches.map((match) => match.replaceAll(/[{}]/g, ''));
}

/**
 * Substitutes payload values into a `{{variable}}` template.
 *
 * ```ts
 * substituteVariables('There are {{free}} desks', data, { missing: 'na', replace });
 * ```
 *
 * A value counts as present when it is a `string`, `number` or `boolean`. Legacy tested
 * `data[key] !== undefined`, which let a `null` reach the screen as the literal text
 * `null` and an object as `[object Object]`; those are treated as missing here, so a broker
 * sending a null renders `N/A` (or the bare variable name) instead. Recorded in
 * docs/MIGRATION-WIDGETS.md §8, W-17.
 *
 * Replacements are applied through a replacer function rather than a string, so a `$&` or
 * `$1` in a *payload value* is inserted literally instead of being expanded by
 * `String.replace`. No deployed config or payload contains one; this only removes the
 * possibility that a broker message can rewrite the template around it.
 */
export function substituteVariables(
  text: string,
  data: Record<string, unknown>,
  { missing, replace }: SubstituteOptions,
): string {
  let result = text;

  for (const name of findVariableInText(text)) {
    const value = data[name];
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      const resolved = String(value);
      result = result.replace(`{{${name}}}`, () => resolved);
    } else if (missing === 'na') {
      result = result.replace(`{{${name}}}`, () => 'N/A');
    }
    // `'strip'` leaves the unresolved variable in place; the brace strip below exposes it.
  }

  if (missing === 'strip') result = result.replaceAll(/[{}]/g, '');

  for (const { find, replace: replacement } of replace ?? []) {
    result = result.replace(find, () => replacement);
  }

  return result;
}

/**
 * The pre-data placeholder for `'strip'`-mode widgets: every variable renders as `0`.
 *
 * `'There are {{free}} desks'` -> `'There are 0 desks'`. This is the legacy loading state —
 * `HourlyChartWidget` and friends initialise their text with it and overwrite it on the first
 * message — and docs/MIGRATION-WIDGETS.md §9 requires it to be reproduced exactly: it is what
 * an operator sees on a screen whose broker feed has died, and how a dead feed is recognised.
 *
 * The trailing brace strip is part of the legacy expression and is what removes braces the
 * variable pattern did not match (`{{people_count}}` -> `people_count`).
 */
export function placeholderText(text: string): string {
  return text.replaceAll(VARIABLE_PATTERN, '0').replaceAll(/[{}]/g, '');
}
